/**
 * OnboardingWizard Component
 * Guides new users through creating their first workspace, project, and task
 */

import { useOnboarding } from '@/contexts/OnboardingContext.context'
import { OnboardingWorkspaceStep } from '@/components/onboarding/OnboardingWorkspaceStep'
import { OnboardingProjectStep } from '@/components/onboarding/OnboardingProjectStep'
import { OnboardingTaskStep } from '@/components/onboarding/OnboardingTaskStep'
import { OnboardingComplete } from '@/components/onboarding/OnboardingComplete'
import { Card } from '@/components/ui/card'
import { useNavigate } from 'react-router-dom'

export function OnboardingWizard() {
  const { currentStep, workspaceName, projectName, taskName, skipOnboarding, completeOnboarding } =
    useOnboarding()
  const navigate = useNavigate()

  // Step 1: Skip entire onboarding (nothing created)
  const handleSkipWorkspace = () => {
    skipOnboarding()
    navigate('/')
  }

  // Step 2: Workspace already created, skip remaining steps (project & task)
  const handleSkipProject = () => {
    completeOnboarding()
    navigate('/')
  }

  // Step 3: Workspace & project already created, skip remaining steps (task)
  const handleSkipTask = () => {
    completeOnboarding()
    navigate('/')
  }

  const getStepNumber = () => {
    switch (currentStep) {
      case 'workspace':
        return 1
      case 'project':
        return 2
      case 'task':
        return 3
      case 'complete':
        return 3
      default:
        return 1
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-6 sm:p-8">
        {/* Progress indicator */}
        {currentStep !== 'complete' && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold">Welcome to Axioma!</h2>
              <span className="text-sm text-muted-foreground">Step {getStepNumber()} of 3</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 mb-3">
              <div
                className="bg-primary rounded-full h-2 transition-all duration-300"
                style={{ width: `${(getStepNumber() / 3) * 100}%` }}
              />
            </div>

            {/* Progress Summary */}
            {(workspaceName || projectName || taskName) && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
                <p className="text-xs font-medium text-muted-foreground mb-2">Your Progress:</p>
                {workspaceName && (
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    <span className="text-muted-foreground">Workspace:</span>
                    <span className="font-medium">{workspaceName}</span>
                  </div>
                )}
                {projectName && (
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    <span className="text-muted-foreground">Project:</span>
                    <span className="font-medium">{projectName}</span>
                  </div>
                )}
                {taskName && (
                  <div className="flex items-center gap-1">
                    <span className="text-green-600">✓</span>
                    <span className="text-muted-foreground">Task:</span>
                    <span className="font-medium">{taskName}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step content */}
        <div className="space-y-6">
          {currentStep === 'workspace' && (
            <OnboardingWorkspaceStep onSkip={handleSkipWorkspace} skipLabel="Skip All" />
          )}
          {currentStep === 'project' && (
            <OnboardingProjectStep onSkip={handleSkipProject} skipLabel="Skip Remaining" />
          )}
          {currentStep === 'task' && (
            <OnboardingTaskStep onSkip={handleSkipTask} skipLabel="Skip Adding Task" />
          )}
          {currentStep === 'complete' && <OnboardingComplete />}
        </div>
      </Card>
    </div>
  )
}
